import { useNavigate } from 'react-router-dom';
import { Image as ImageIcon, Wrench, MapPin, ExternalLink, Trash2, Repeat, Gauge, Banknote } from 'lucide-react';
import { useTranslations } from '../../i18n';
import { theme } from '../../lib/theme';
import { formatCurrency } from '../../lib/conversions';
import { DynamicIcon } from '../../lib/icons';
import { normalizeExpenseTypeKey, getExpenseTypeNameFromData, SupportedLanguage } from '../../lib/expenseTypeHelpers';
import defaultExpenseTypesData from '../../data/defaultExpenseTypes.json';
import { LogEntry } from '../../lib/supabase';
import { formatDate } from '../../lib/timeUtils';

export interface LogEntryItemProps {
    entry: LogEntry;
    slug: string;
    isAdminView?: boolean;
    effectivePreferences: any;
    openInNewTab?: boolean;
    onClick?: () => void;
    className?: string;
    highlighted?: boolean;
    amountOverride?: number;
    descriptionOverride?: string;
    showActions?: boolean;
    onDelete?: () => void;
    isPublicView?: boolean;
    shareLocationsEnabled?: boolean;
    forceShowYear?: boolean;
}

export function LogEntryItem({
    entry,
    slug,
    isAdminView = false,
    effectivePreferences,
    openInNewTab = false,
    onClick,
    className = "",
    highlighted = false,
    amountOverride,
    descriptionOverride,
    showActions = false,
    onDelete,
    isPublicView = false,
    shareLocationsEnabled = false,
    forceShowYear = false
}: LogEntryItemProps) {
    const navigate = useNavigate();
    const { t, language } = useTranslations();
    const currentLanguage = language as SupportedLanguage;

    const getExpenseTypeInfo = (typeKey: string) => {
        const key = normalizeExpenseTypeKey(typeKey);
        return (defaultExpenseTypesData as any)[key];
    };

    const getTypeName = (type: string) => {
        const info = getExpenseTypeInfo(type);
        if (info) return getExpenseTypeNameFromData(info, currentLanguage);
        return (t.expenses.typeLabels as any)[type] || type;
    };

    const handleOpenNewTab = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        const prefix = isAdminView ? '/admin' : '';
        const url = `${prefix}/${slug}/expenses/${entry.id}`;
        window.open(url, '_blank');
    };

    const handleDelete = (e: React.MouseEvent) => {
        e.preventDefault();
        e.stopPropagation();
        if (onDelete) onDelete();
    };

    const handleClick = (e: React.MouseEvent) => {
        if (onClick) {
            onClick();
            return;
        }

        e.preventDefault();
        e.stopPropagation();
        const prefix = isAdminView ? '/admin' : '';
        const url = `${prefix}/${slug}/expenses/${entry.id}`;

        if (openInNewTab) {
            window.open(url, '_blank');
        } else {
            navigate(url);
        }
    };

    const expenses = entry.expenses || [];
    const totalAmount = expenses.reduce((sum, exp) => sum + (exp.amount || 0), 0);
    const allInfo = expenses.length > 0 && expenses.every(e => e.type === 'info');

    // Pick the first expense type for icon if there are multiple, or a default
    const principalExpense = expenses.length > 0 ? expenses[0] : null;
    const typeInfo = principalExpense ? getExpenseTypeInfo(principalExpense.type) : null;
    const displayTypeInfo = typeInfo || { icon: 'List', color: '#6B7280' };

    // Generate title: explicit title OR combine expense types
    let displayTitle = entry.title;
    if (!displayTitle) {
        if (expenses.length > 0) {
            displayTitle = Array.from(new Set(expenses.map(e => getTypeName(e.type)))).join(', ');
        } else {
            displayTitle = t.search.untitledExpense; // fallback
        }
    }

    const manualDescription = expenses
        .map(e => e.description?.trim())
        .filter(Boolean)
        .join(', ');

    const canShowLocation = !isPublicView || shareLocationsEnabled || isAdminView;

    let locationPart = '';
    if (entry.location && canShowLocation) {
        const loc = entry.location as any;
        const streetPart = loc.address ? loc.address.split(',')[0].trim() : '';
        const addressDetails = [streetPart, loc.city].filter(Boolean).join(', ');
        locationPart = [loc.name, addressDetails].filter(Boolean).join(' - ');
    }

    let displayDescription = manualDescription;
    
    if (locationPart) {
        if (!displayDescription) {
            displayDescription = locationPart;
        } else {
            displayDescription = `${displayDescription}\n${locationPart}`;
        }
    }

    const partInfos = (entry as any).log_entry_parts
        ?.map((p: any) => {
            const part = p.spare_parts;
            if (!part) return null;
            return part.manufacturer ? `${part.name} (${part.manufacturer})` : part.name;
        })
        .filter(Boolean) || [];

    if (partInfos.length > 0) {
        const partsInfo = partInfos.join('\n');
        if (!displayDescription) {
            displayDescription = partsInfo;
        } else {
            displayDescription = `${displayDescription}\n${partsInfo}`;
        }
    }

    if (!displayDescription && allInfo) {
        displayDescription = '';
    }

    // Unit price calculation (price per L/gal or kWh)
    const fuelExp = expenses.find(e => e.type === 'fuel' && typeof e.fuel_liters === 'number' && e.fuel_liters > 0);
    const elecExp = expenses.find(e => e.type === 'electricity' && typeof e.electricity_kwh === 'number' && e.electricity_kwh > 0);

    const quantityTag = (() => {
        if (fuelExp) {
            const unit = effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'l';
            return `${fuelExp.fuel_liters} ${unit}`;
        }
        if (elecExp) {
            return `${elecExp.electricity_kwh} kWh`;
        }
        return null;
    })();

    const unitPriceTag = (() => {
        if (fuelExp) {
            const up = (fuelExp.amount || 0) / fuelExp.fuel_liters!;
            const unit = effectivePreferences?.volume_unit === 'gallons' ? 'gal' : 'L';
            const currency = effectivePreferences?.currency_symbol || '€';
            return `${up.toFixed(3)} ${currency}/${unit}`;
        }
        if (elecExp) {
            const up = (elecExp.amount || 0) / elecExp.electricity_kwh!;
            const currency = effectivePreferences?.currency_symbol || '€';
            return `${up.toFixed(3)} ${currency}/kWh`;
        }
        return null;
    })();

    // Data tags (mileage and value)
    const dataTags = (() => {
        const tags = [];
        const entryValue = (typeof entry.car_value === 'number' && entry.car_value > 0)
            ? entry.car_value
            : null;

        if (entryValue !== null) {
            tags.push({
                icon: <Banknote size={12} className="mr-1" />,
                text: effectivePreferences ? formatCurrency(entryValue, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${entryValue.toFixed(2)}`
            });
        }
        const entryMileage = (typeof entry.mileage === 'number' && entry.mileage > 0)
            ? entry.mileage
            : null;

        if (entryMileage !== null) {
            tags.push({
                icon: <Gauge size={12} className="mr-1" />,
                text: `${entryMileage.toLocaleString()} ${effectivePreferences?.distance_unit === 'miles' ? (t.common?.milesShort || 'mi') : (t.common?.kmShort || 'km')}`
            });
        }
        return tags.length > 0 ? tags : null;
    })();


    const RenderDataTags = () => (
        <div className="flex items-center gap-1.5 flex-wrap">
            {dataTags?.map((tag: any, idx: number) => (
                <span key={idx} className="text-[12px] font-semibold text-gray-500 dark:text-gray-400 bg-gray-100 dark:bg-white/5 px-2 py-0.5 rounded uppercase tracking-widest flex-shrink-0 flex items-center">
                    {tag.icon}
                    {tag.text}
                </span>
            ))}
        </div>
    );

    return (
        <button
            type="button"
            onClick={handleClick}
            className={`w-full group text-left flex items-center gap-4 ${theme.spacing.cardPadding} transition-all duration-700 hover:bg-gray-50/80 dark:hover:bg-white/5 active:scale-[0.99] ${highlighted ? 'bg-yellow-50/80 dark:bg-yellow-900/20 z-10' : 'bg-transparent'} ${className}`}
        >
            <div
                className="w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: `${displayTypeInfo?.color || '#F3F4F6'}15` }}
            >
                <DynamicIcon name={expenses.length > 1 ? 'Layers' : (displayTypeInfo?.icon || 'Circle')} size={20} color={displayTypeInfo?.color || '#6B7280'} />
            </div>

            <div className="flex-1 min-w-0 flex justify-between items-start gap-3">
                <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-0.5">
                        <h4 className={`${theme.typography.itemTitle} truncate`}>
                            {displayTitle}
                        </h4>
                        {unitPriceTag && (
                            <span className="text-[12px] font-semibold text-gray-400 bg-gray-100 dark:bg-white/5 px-2 py-0.5 rounded uppercase tracking-widest flex-shrink-0">
                                {unitPriceTag}
                            </span>
                        )}
                    </div>
                    
                    {dataTags && (
                        <div className="mb-1">
                            <RenderDataTags />
                        </div>
                    )}

                    {descriptionOverride ? (
                        <p className={`${theme.typography.itemSubtitle} line-clamp-3 whitespace-pre-line`}>
                            {descriptionOverride}
                        </p>
                    ) : displayDescription ? (
                        <p className={`${theme.typography.itemSubtitle} line-clamp-3 whitespace-pre-line`}>
                            {displayDescription}
                        </p>
                    ) : null}
                </div>

                <div className="text-right flex-shrink-0 flex flex-col items-end gap-0.5">
                    {!allInfo && (
                        <span className={theme.typography.itemTitle}>
                            {quantityTag && (
                                <span className="text-gray-400 dark:text-gray-500 text-[13px] font-semibold mr-1.5 uppercase tracking-tight">
                                    {quantityTag}
                                    <span className="mx-1.5 opacity-40 font-medium">•</span>
                                </span>
                            )}
                            {effectivePreferences ? formatCurrency(amountOverride ?? totalAmount, effectivePreferences.currency, effectivePreferences.currency_symbol) : `€${(amountOverride ?? totalAmount).toFixed(2)}`}
                        </span>
                    )}
                    <span className={`${theme.typography.itemSubtitle} whitespace-nowrap`}>
                        {entry.date ? (() => {
                            // If we have explicit time_used flag, use it
                            if (entry.time_used !== undefined && entry.time_used !== null) {
                                return formatDate(entry.date, currentLanguage, effectivePreferences, forceShowYear, entry.time_used);
                            }
                            
                            // Legacy fallback guessing logic
                            const hasTime = entry.date.includes('T') && 
                                           !entry.date.endsWith('T00:00:00.000Z') && 
                                           !entry.date.endsWith('T00:00:00Z');
                            
                            return formatDate(entry.date, currentLanguage, effectivePreferences, forceShowYear, hasTime);
                        })() : ''}
                    </span>
                    {((entry.imageCount || 0) > 0 || (entry.partCount || 0) > 0 || !!entry.location_id || !!entry.repeat_id) && (
                        <div className="flex items-center gap-2 mt-0.5">
                            {(entry.imageCount || 0) > 0 && (
                                <span className="flex items-center gap-1 text-blue-500 dark:text-blue-400">
                                    <ImageIcon size={14} />
                                    <span className="text-xs font-medium">{entry.imageCount}</span>
                                </span>
                            )}
                            {(entry.partCount || 0) > 0 && (
                                <span className="flex items-center gap-1 text-orange-500">
                                    <Wrench size={14} />
                                    <span className="text-xs font-medium">{entry.partCount}</span>
                                </span>
                            )}
                            {entry.location_id && canShowLocation && (
                                <span className="flex items-center gap-1 text-emerald-500 dark:text-emerald-400">
                                    <MapPin size={14} />
                                </span>
                            )}
                            {entry.repeat_id && (
                                <span className="flex items-center gap-1 text-blue-500" title={t.repeatingExpenses?.isRepeating || 'Repeating'}>
                                    <Repeat size={14} />
                                </span>
                            )}
                        </div>
                    )}
                </div>

                {showActions && (
                    <div className="flex items-center self-stretch gap-1 ml-1 pl-3 border-l border-gray-100 dark:border-white/10">
                        <button
                            onClick={handleOpenNewTab}
                            className="p-2 text-gray-400 hover:text-blue-500 hover:bg-blue-50 dark:hover:bg-blue-500/10 rounded-lg transition-all"
                            title={t.common?.openInNewTab || 'Open in new tab'}
                        >
                            <ExternalLink size={18} />
                        </button>
                        <button
                            onClick={handleDelete}
                            className="p-2 text-gray-400 hover:text-red-500 hover:bg-red-50 dark:hover:bg-red-500/10 rounded-lg transition-all"
                            title={t.common?.delete || 'Delete'}
                        >
                            <Trash2 size={18} />
                        </button>
                    </div>
                )}
            </div>
        </button>
    );
}
